import { useEffect, useState } from "react";
import { Instagram, Music2, Facebook, ExternalLink, Save, Sparkles, RefreshCw, AlertTriangle, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";

const PLATFORMS = [
  {
    id: "instagram", name: "Instagram Shopping", icon: Instagram, color: "#E4405F",
    description: "Etiqueta productos en publicaciones, Reels y Stories. Sincroniza catálogo con Meta Commerce Manager.",
    fields: [
      { name: "access_token", label: "Page Access Token (Long-lived)", type: "password", help: "Obtén en developers.facebook.com → tu app → Tokens" },
      { name: "business_account_id", label: "Instagram Business Account ID", help: "ID numérico de tu cuenta business" },
      { name: "catalog_id", label: "Catalog ID (Meta Commerce)", help: "Lo encuentras en Commerce Manager" },
      { name: "page_id", label: "Facebook Page ID", help: "Tu página de Facebook vinculada" },
    ],
    setupSteps: [
      "Crea una App en developers.facebook.com",
      "Vincula tu cuenta de Instagram Business a una página de Facebook",
      "Activa Commerce Manager y crea un catálogo",
      "Genera un Page Access Token de larga duración",
      "Copia los IDs y guárdalos aquí",
    ],
    docsUrl: "https://developers.facebook.com/docs/instagram-api/guides/shopping",
  },
  {
    id: "tiktok", name: "TikTok Shop", icon: Music2, color: "#000",
    description: "Vende productos directamente en TikTok. Etiqueta en vídeos y directos. Requiere TikTok Seller Center activo.",
    fields: [
      { name: "shop_id", label: "Shop ID", help: "ID de tu tienda en TikTok Seller Center" },
      { name: "app_key", label: "App Key", help: "Clave de aplicación de TikTok Developer Portal" },
      { name: "app_secret", label: "App Secret", type: "password", help: "Secreto de aplicación" },
      { name: "access_token", label: "Access Token", type: "password", help: "Token de acceso autenticado" },
    ],
    setupSteps: [
      "Regístrate en TikTok Seller Center (España: seller-es.tiktok.com)",
      "Solicita acceso a TikTok Shop API Developer Portal",
      "Crea una App y solicita scopes de catálogo y pedidos",
      "Realiza el OAuth y obtén un Access Token",
      "Copia el Shop ID, App Key/Secret y Token aquí",
    ],
    docsUrl: "https://partner.tiktokshop.com/docv2/page/welcome",
  },
  {
    id: "facebook", name: "Facebook Shop", icon: Facebook, color: "#1877F2",
    description: "Sincroniza tu catálogo con tu tienda de Facebook. Comparte la configuración con Instagram (Meta).",
    fields: [
      { name: "access_token", label: "Page Access Token", type: "password", help: "Comparte con Instagram (Meta)" },
      { name: "catalog_id", label: "Catalog ID", help: "Mismo catálogo de Commerce Manager" },
      { name: "page_id", label: "Page ID", help: "ID de tu página de Facebook" },
    ],
    setupSteps: [
      "Habilita Facebook Shop desde Commerce Manager",
      "Reutiliza el mismo catálogo que usas para Instagram",
      "El token de página sirve para ambas plataformas",
    ],
    docsUrl: "https://www.facebook.com/business/shops",
  },
  {
    id: "pinterest", name: "Pinterest Shopping", icon: Sparkles, color: "#E60023",
    description: "Convierte tus pines en compras directas con Rich Pins de producto.",
    fields: [
      { name: "access_token", label: "Access Token", type: "password" },
      { name: "board_id", label: "Board ID por defecto", help: "ID del tablero donde se publicarán nuevos productos" },
    ],
    setupSteps: [
      "Aplica a Pinterest Business",
      "Solicita acceso a la Pinterest API en developers.pinterest.com",
      "Genera un Access Token con scope de catálogos",
    ],
    docsUrl: "https://developers.pinterest.com/docs/shopping/",
  },
];


export default function AdminSocial() {
  const [configs, setConfigs] = useState({});
  const [activePlatform, setActivePlatform] = useState("instagram");
  const [logs, setLogs] = useState([]);
  const [syncing, setSyncing] = useState(false);

  const load = () => api.get("/admin/social/configs").then(r => setConfigs(r.data || {}));
  const loadLogs = () => api.get("/admin/social/sync-logs").then(r => setLogs(r.data || []));

  useEffect(() => { load(); loadLogs(); }, []);

  const platform = PLATFORMS.find(p => p.id === activePlatform);
  const data = configs[activePlatform] || { platform: activePlatform, enabled: true };

  const updateField = (key, val) => {
    setConfigs(c => ({
      ...c,
      [activePlatform]: { ...(c[activePlatform] || { platform: activePlatform, enabled: true }), [key]: val },
    }));
  };

  const save = async () => {
    try {
      await api.put(`/admin/social/configs/${activePlatform}`, {
        ...data,
        platform: activePlatform,
        enabled: data.enabled !== false,
      });
      toast.success("Configuración guardada");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const { data: r } = await api.post(`/admin/social/sync/${activePlatform}`);
      if (r.status === "completed") {
        toast.success(r.message);
      } else {
        toast.warning(r.message);
      }
      loadLogs();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSyncing(false); }
  };

  const isConfigured = data.access_token || data.app_secret;

  return (
    <div data-testid="admin-social">
      <p className="overline mb-3">Social Commerce</p>
      <h1 className="font-heading text-4xl text-ivory mb-3">Conectar redes sociales</h1>
      <p className="text-sm text-ivory/60 mb-8 max-w-2xl">Sincroniza tu catálogo con Instagram, TikTok, Facebook y Pinterest. Etiqueta productos en publicaciones, Reels, vídeos y pines.</p>

      {/* Platform tabs */}
      <div className="flex gap-2 mb-8 flex-wrap" data-testid="platform-tabs">
        {PLATFORMS.map(p => {
          const cfg = configs[p.id];
          const configured = cfg && (cfg.access_token || cfg.app_secret);
          return (
            <button
              key={p.id}
              onClick={() => setActivePlatform(p.id)}
              data-testid={`tab-${p.id}`}
              className={`flex items-center gap-3 px-5 py-3 border transition-colors ${
                activePlatform === p.id ? "border-gold text-gold bg-graphite" : "border-smoke text-ivory/60 hover:text-ivory"
              }`}
            >
              <p.icon size={18} />
              <span className="text-xs uppercase tracking-[0.15em]">{p.name}</span>
              {configured && <CheckCircle2 size={14} className="text-gold" />}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Config form */}
        <div className="lg:col-span-2 bg-graphite border border-smoke">
          <div className="p-6 border-b border-smoke">
            <div className="flex items-center gap-3 mb-2">
              <platform.icon size={24} className="text-gold" />
              <h2 className="font-heading text-2xl text-ivory">{platform.name}</h2>
              {isConfigured ? (
                <span className="ml-auto text-[10px] uppercase tracking-[0.2em] text-gold border border-gold px-2 py-1 flex items-center gap-1">
                  <CheckCircle2 size={11} /> Configurado
                </span>
              ) : (
                <span className="ml-auto text-[10px] uppercase tracking-[0.2em] text-ivory/50 border border-smoke px-2 py-1 flex items-center gap-1">
                  <AlertTriangle size={11} /> Sin configurar
                </span>
              )}
            </div>
            <p className="text-sm text-ivory/60">{platform.description}</p>
          </div>

          <div className="p-6 space-y-5">
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={data.enabled !== false}
                onChange={e => updateField("enabled", e.target.checked)}
                className="accent-gold w-4 h-4"
                data-testid="enabled-toggle"
              />
              <span className="text-ivory">Activar integración con {platform.name}</span>
            </label>

            {platform.fields.map(f => (
              <div key={f.name}>
                <label className="luxe-label">{f.label}</label>
                <input
                  data-testid={`field-${f.name}`}
                  type={f.type || "text"}
                  value={data[f.name] || ""}
                  onChange={e => updateField(f.name, e.target.value)}
                  className="luxe-input font-mono text-sm"
                  placeholder={f.type === "password" ? "••••••••" : ""}
                />
                {f.help && <p className="text-xs text-ivory/40 mt-1">{f.help}</p>}
              </div>
            ))}

            <div className="border-t border-smoke pt-5">
              <p className="overline mb-3">Configuración automática</p>
              <label className="flex items-center gap-3 mb-3">
                <input
                  type="checkbox"
                  checked={data.auto_publish || false}
                  onChange={e => updateField("auto_publish", e.target.checked)}
                  className="accent-gold w-4 h-4"
                  data-testid="auto-publish"
                />
                <span className="text-ivory text-sm">Publicar automáticamente al crear un producto nuevo</span>
              </label>
              <label className="luxe-label">Plantilla de caption por defecto</label>
              <textarea
                value={data.default_caption_template || ""}
                onChange={e => updateField("default_caption_template", e.target.value)}
                className="luxe-input border border-smoke px-3 py-3 min-h-[80px]"
                placeholder="✨ Nuevo en M&H Lumora: {product_name}&#10;{description}&#10;Precio: {price}€"
              />
              <p className="text-xs text-ivory/40 mt-1">Variables disponibles: {"{product_name}, {description}, {price}, {brand}"}</p>
              <label className="luxe-label mt-4">Hashtags por defecto</label>
              <input
                value={data.default_hashtags || ""}
                onChange={e => updateField("default_hashtags", e.target.value)}
                className="luxe-input"
                placeholder="#MHLumora #Cosmetica #Belleza"
              />
            </div>
          </div>

          <div className="p-6 border-t border-smoke flex gap-3">
            <button onClick={save} className="btn-gold flex items-center gap-2" data-testid="save-social">
              <Save size={14} /> Guardar configuración
            </button>
            <button
              onClick={sync}
              disabled={syncing}
              className="btn-outline-gold flex items-center gap-2"
              data-testid="sync-now"
            >
              {syncing ? <RefreshCw size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              Sincronizar productos
            </button>
          </div>
        </div>

        {/* Setup guide + Logs */}
        <div className="space-y-5">
          <div className="bg-graphite border border-smoke p-6">
            <p className="overline mb-3">Cómo configurar</p>
            <ol className="space-y-3 text-sm text-ivory/80">
              {platform.setupSteps.map((step, i) => (
                <li key={i} className="flex gap-3">
                  <span className="text-gold font-heading text-lg leading-none">{i + 1}.</span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
            <a
              href={platform.docsUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-5 inline-flex items-center gap-2 text-gold text-xs uppercase tracking-[0.2em] hover:underline"
            >
              Documentación oficial <ExternalLink size={12} />
            </a>
          </div>

          {logs.length > 0 && (
            <div className="bg-graphite border border-smoke p-6">
              <p className="overline mb-3">Últimas sincronizaciones</p>
              <div className="space-y-3">
                {logs.slice(0, 6).map(l => (
                  <div key={l.id} className="border-l-2 border-smoke pl-3 text-xs" data-testid={`log-${l.id}`}>
                    <p className="text-ivory">
                      <span className={`uppercase tracking-wider ${l.status === "completed" ? "text-gold" : "text-rose"}`}>{l.platform}</span>
                      <span className="text-ivory/40 ml-2">{new Date(l.started_at).toLocaleString("es-ES")}</span>
                    </p>
                    <p className="text-ivory/60 mt-1">{l.message}</p>
                    <p className="text-ivory/40 mt-1">{l.synced}/{l.total_products} productos</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
